import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { mapDocument } from '@/features/patient-record/mappers/document';
import type { DocumentType, DocumentViewModel } from '@/features/patient-record/types';
import { patientQueries } from "@/features/patient-record/queries";

export interface UploadDocumentVariables {
  readonly title: string;
  readonly documentType: DocumentType;
  readonly file?: File;
}

interface MutationContext {
  readonly previous: DocumentViewModel[] | undefined;
}

export function useUploadDocument(
  guid: string,
): UseMutationResult<
  DocumentViewModel,
  Error,
  UploadDocumentVariables,
  MutationContext
> {
  const queryClient = useQueryClient();
  const queryKey = patientQueries.documents(guid).queryKey;

  return useMutation<
    DocumentViewModel,
    Error,
    UploadDocumentVariables,
    MutationContext
  >({
    meta: { successMessage: 'success.toast.title.document_created' },
    mutationFn: async (variables) => {
      const dto = await apiClient.uploadDocument(guid, {
        title: variables.title,
        document_type: variables.documentType,
        ...(variables.file !== undefined && { file: variables.file }),
      });
      const mapped = mapDocument(dto);
      // Preserve the user-supplied type/title when the BE response omits
      // them on 201 — narrowEnum/mapDocument default to OTHER + filename,
      // which would silently overwrite what the user just chose
      // (PTV2-1121). The user's input is the most accurate source we have
      // at the moment of upload success.
      const hasResponseType =
        dto.type !== undefined && dto.type !== null && dto.type.length > 0;
      const hasResponseComments =
        dto.comments !== undefined && dto.comments !== null && dto.comments.length > 0;
      return {
        ...mapped,
        documentType: hasResponseType ? mapped.documentType : variables.documentType,
        title: hasResponseComments ? mapped.title : variables.title,
      };
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);
      const now = new Date();
      const optimistic: DocumentViewModel = {
        id: `optimistic-${Date.now().toString(36)}`,
        documentType: variables.documentType,
        title: variables.title,
        fileName: variables.file?.name ?? null,
        createdAt: `${String(now.getFullYear()).padStart(4, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`,
        createdBy: null,
      };
      queryClient.setQueryData(queryKey, [
        optimistic,
        ...(previous ?? []),
      ]);
      return { previous };
    },
    onSuccess: (created) => {
      // Replace the optimistic placeholder with the just-created doc so the
      // user-supplied title/type stick around even if the BE list endpoint
      // omits them on the next refetch (PTV2-1121). We deliberately don't
      // invalidate on success — trust the local cache for the brand-new row
      // and let the next natural refresh reconcile.
      queryClient.setQueryData<DocumentViewModel[]>(queryKey, (current) => {
        if (current === undefined) return [created];
        const optimisticIdx = current.findIndex((doc) =>
          doc.id.startsWith('optimistic-'),
        );
        if (optimisticIdx < 0) return [created, ...current];
        const next = current.slice();
        next[optimisticIdx] = created;
        return next;
      });
    },
    onError: (_error, _vars, context) => {
      if (context === undefined) return;
      queryClient.setQueryData(
        queryKey,
        context.previous,
      );
      queryClient.invalidateQueries({ queryKey }).catch(() => undefined);
    },
  });
}

export function useDeleteDocument(
  guid: string,
): UseMutationResult<undefined, Error, string, MutationContext> {
  const queryClient = useQueryClient();
  const queryKey = patientQueries.documents(guid).queryKey;

  return useMutation<undefined, Error, string, MutationContext>({
    meta: { successMessage: 'success.toast.title.document_deleted' },
    mutationFn: (id) => apiClient.deleteDocument(guid, id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(
        queryKey,
        (previous ?? []).filter((d) => d.id !== id),
      );
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context === undefined) return;
      queryClient.setQueryData(
        queryKey,
        context.previous,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey }).catch(() => undefined);
    },
  });
}
